import { inject as service } from '@ember/service';
import Controller, { inject as controller } from '@ember/controller';
import { computed, action } from '@ember/object';
import { task } from 'ember-concurrency';
import AuthService from 'cio/services/auth';
import EnvironmentModel from 'cio/models/environment';
import SegmentAuthService, {
  SegmentAuthAction,
  SegmentAuthenticateResponse,
  SegmentAuthResult,
  SegmentIntegration,
} from 'cio/services/segment-auth';
import ApiCredentialModel from 'cio/models/api-credential';
import AlertsService from 'cio/services/alerts';
import ApiCredentialsService from 'cio/services/api-credentials';
import ApiService from 'cio/services/api';
import AnalyticsService from 'cio/services/analytics';
import moment from 'moment-timezone';
import IntegrationsController from 'cio/pods/integrations/index/controller';
import EnvironmentService from 'cio/services/environment';
import AppController from 'cio/pods/app/controller';
import { joinWordsAsSnippets } from 'cio/utils/join-words';
import DS from 'ember-data';

type Alert = {
  type: 'success' | 'error';
  title: string;
  body: string;
};

enum ConnectionState {
  Exists = 'exists',
  Enabled = 'enabled',
  Disabled = 'disabled',
  Deleted = 'deleted',
  Unknown = 'unknown',
}

enum ModalState {
  Closed = 'closed',
  Delete = 'delete',
  Disable = 'disable',
  Clear = 'clear',
}

const INTEGRATION_META = {
  name: 'Segment',
  category: 'Data management',
  subCategory: 'Destination',
};

export default class IntegrationsSegmentInIndexController extends Controller {
  @service alerts!: AlertsService;
  @service auth!: AuthService;
  @service segmentAuth!: SegmentAuthService;
  @service apiCredentials!: ApiCredentialsService;
  @service api!: ApiService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  @controller('integrations.index') integrationsController!: IntegrationsController;
  @controller('app') appController!: AppController;

  // Props
  alert: Alert | null = null;
  dropdownActions = {
    disable: {
      label: 'Disable Connection',
      value: 'disable',
    },
    delete: {
      label: 'Delete Connection',
      value: 'delete',
    },
  };

  // Controller state
  model!: EnvironmentModel;
  _selectedCredential: ApiCredentialModel | null = null;
  modal: ModalState = ModalState.Closed;
  // This needs to be stored so that closing the OAuth popup doesn't create a bad/invalid state
  latestAuthenticateResponse: SegmentAuthenticateResponse | null = null;
  segmentImage = new URL('cio/assets/images/integrations/segment.svg', import.meta.url).toString();

  @computed('apiCredentials.credentials', '_selectedCredential')
  get selectedCredential(): ApiCredentialModel | null {
    if (this.apiCredentials.credentials.length === 1) {
      return this.apiCredentials.credentials.get('firstObject') ?? null;
    }

    return this._selectedCredential;
  }

  @computed('segmentAuth.segmentIn.isRunning', 'apiCredentials.loadCurrentEnvironmentCredentials.isRunning')
  get isLoading() {
    return this.segmentAuth.segmentIn.isRunning || this.apiCredentials.loadCurrentEnvironmentCredentials.isRunning;
  }

  @computed('segmentAuth.segmentIn.last.value', 'connectionStatus')
  get lastVerifiedTime() {
    let { last } = this.segmentAuth.segmentIn;
    let status = this.connectionStatus;

    if (!last?.value) {
      return;
    }

    if (status === ConnectionState.Unknown || status === ConnectionState.Deleted) {
      return;
    }

    return last.value.updated_at;
  }

  @computed('segmentAuth.segmentIn.last.value')
  get lastConnectedSource() {
    let { last } = this.segmentAuth.segmentIn;

    if (!last?.value) {
      return;
    }

    return this.parseSegmentWorkspace(last.value.source);
  }

  @computed('segmentAuth.segmentIn.last.value', 'latestAuthenticateResponse')
  get connectionStatus(): ConnectionState {
    if (this.latestAuthenticateResponse) {
      if (this.latestAuthenticateResponse.state === SegmentAuthResult.Created) {
        return ConnectionState.Enabled;
      } else if (this.latestAuthenticateResponse.state === SegmentAuthResult.Deleted) {
        return ConnectionState.Deleted;
      } else if (this.latestAuthenticateResponse.state === SegmentAuthResult.Disabled) {
        return ConnectionState.Disabled;
      } else if (this.latestAuthenticateResponse.state === SegmentAuthResult.Enabled) {
        return ConnectionState.Enabled;
      } else if (this.latestAuthenticateResponse.state === SegmentAuthResult.Errored) {
        if (this.latestAuthenticateResponse.enabled === false) {
          return ConnectionState.Disabled;
        } else if (
          this.latestAuthenticateResponse.errors &&
          (this.latestAuthenticateResponse.errors[0].detail === 'destination not found' ||
            this.latestAuthenticateResponse.errors[0].status === '404')
        ) {
          return ConnectionState.Deleted;
        } else if (
          this.latestAuthenticateResponse.errors &&
          this.latestAuthenticateResponse.errors[0].detail === 'source mismatch'
        ) {
          return ConnectionState.Exists;
        }
      }
    } else if (this.segmentAuth.segmentIn.last?.value) {
      return ConnectionState.Exists;
    }

    return ConnectionState.Unknown;
  }

  @computed('segmentAuth.segmentIn.last.value')
  get isExistent() {
    return this.segmentAuth.segmentIn.last?.value;
  }

  @computed('connectionStatus')
  get isDeleteDisablePossible() {
    switch (this.connectionStatus) {
      case ConnectionState.Exists:
      case ConnectionState.Enabled:
      case ConnectionState.Disabled:
        return true;
      default:
        return false;
    }
  }

  @computed('connectionStatus')
  get buttonLabel() {
    if (this.connectionStatus === ConnectionState.Exists) {
      return 'Verify Connection Status';
    } else if (this.connectionStatus === ConnectionState.Enabled) {
      return 'Connected to Segment';
    } else if (this.connectionStatus === ConnectionState.Disabled) {
      return 'Re-enable Segment';
    }

    return 'Configure Segment';
  }

  @computed('connectionStatus')
  get descriptionPrefix() {
    if (this.connectionStatus === ConnectionState.Exists) {
      return 'Verify connection to your';
    } else if (this.connectionStatus === ConnectionState.Enabled) {
      return 'Connected to';
    } else if (this.connectionStatus === ConnectionState.Disabled) {
      return 'Re-enable connection to your';
    }

    return 'Connect your';
  }

  @computed('connectionStatus')
  get oauthAction(): SegmentAuthAction {
    if (this.connectionStatus === ConnectionState.Exists) {
      return SegmentAuthAction.Check;
    } else if (this.connectionStatus === ConnectionState.Disabled) {
      return SegmentAuthAction.Enable;
    }

    return SegmentAuthAction.Create;
  }

  get canIntegrateSegment() {
    return this.auth.hasPermission('journeys/segment_integration/create');
  }

  get canEditSegment() {
    return this.auth.hasPermission('journeys/segment_integration/edit');
  }

  get canDeleteSegment() {
    return this.auth.hasPermission('journeys/segment_integration/delete');
  }

  get isMainButtonDisabled() {
    return !this.selectedCredential || this.segmentOAuthIntegrateTask.isRunning || !this.canIntegrateSegment;
  }

  @computed('terminateConnection.isRunning', 'clearConnection.isRunning')
  get isModalButtonDisabled() {
    return this.terminateConnection.isRunning || this.clearConnection.isRunning;
  }

  @computed('environment.current.allEnabledIdentifiers.[]')
  get identifierSnippets() {
    return joinWordsAsSnippets(this.environment.current.allEnabledIdentifiers);
  }

  segmentOAuthIntegrateTask = task(
    async (selectedCredential: ApiCredentialModel, method = SegmentAuthAction.Default) => {
      let enabling =
        method === SegmentAuthAction.Create ||
        method === SegmentAuthAction.Default ||
        method === SegmentAuthAction.Enable;
      let verifying = method === SegmentAuthAction.Check;
      let setupGuide = this.loadSetupGuide.last?.value;

      try {
        if (enabling) {
          this.analytics.track('integration', 'enable_started', {
            ...INTEGRATION_META,
            enabledOrReenabled: method === SegmentAuthAction.Enable ? 'Re-enabled' : 'Enabled',
          });
        } else if (verifying) {
          this.analytics.track('integration', 'verify_started', INTEGRATION_META);
        }

        // Clear old alerts that will be invalidated by the oauth flow
        this.set('alert', null);

        let response = await this.segmentAuth.authenticate.perform(selectedCredential, method);

        // @ts-ignore: existing production code, but typings aren't fully correct; TODO fix them
        this.handleOAuthResponse(response);

        if (enabling) {
          this.analytics.track('integration', 'enabled', {
            ...INTEGRATION_META,
            enabledOrReenabled: method === SegmentAuthAction.Enable ? 'Re-enabled' : 'Enabled',
          });
        } else if (verifying) {
          this.analytics.track('integration', 'verify_completed', {
            ...INTEGRATION_META,
            status:
              // Alerts are split between this controller and the auth service, so search both places
              this.alert?.body ??
              this.alerts.current.find((alert) => alert.id.startsWith('integration.segment_in'))?.message,
          });
        }

        if (
          !setupGuide?.integrationMethod ||
          (setupGuide?.integrationMethod === 'segment' && !setupGuide?.integrationConnectedAt)
        ) {
          setupGuide?.setProperties({
            integrationConnectedAt: moment().unix(),
            integrationMethod: 'segment',
          });
          // Don't stop the rest of this task from running if this fails
          setupGuide?.save().catch();
        }

        // Update the last verified timestamp
        await this.segmentAuth.segmentIn.perform();
      } catch (err) {
        if (typeof err === 'string') {
          this.set('alert', {
            type: 'error',
            title: 'Something went wrong',
            body: 'When we tried to authorize with segment either the window was closed or that account is no longer authorized. Please try again.',
          });
        } else if ('errors' in err) {
          this.handleOAuthResponse(err);
        }

        if (verifying) {
          this.analytics.track('integration', 'verify_completed', {
            ...INTEGRATION_META,
            status:
              // Alerts are split between this controller and the auth service, so search both places
              this.alert?.body ??
              this.alerts.current.find((alert) => alert.id.startsWith('integration.segment_in'))?.message,
          });
        }
      }
    },
  );

  terminateConnection = task(async (method: SegmentAuthAction) => {
    if (!this.selectedCredential) {
      return;
    }

    try {
      let setupGuide = this.loadSetupGuide.last?.value;
      let oauthResponse = await this.segmentAuth.authenticate.perform(this.selectedCredential, method);
      // Update the last verified timestamp
      await this.segmentAuth.segmentIn.perform();

      // Reset the authentication state since it's now disabled or deleted and they need to re-verify
      await this.segmentAuth.authenticate.cancelAll({ resetState: true });

      // @ts-ignore: existing production code, but typings aren't fully correct; TODO fix them
      this.handleOAuthResponse(oauthResponse);
      this.analytics.track('integration', 'disabled', {
        ...INTEGRATION_META,
        disabledOrDeleted: method === SegmentAuthAction.Disable ? 'Disabled' : 'Deleted',
      } as Record<string, any>);

      this.set('modal', ModalState.Closed);

      if (method === SegmentAuthAction.Delete && setupGuide) {
        if (setupGuide.integrationMethod === 'segment') {
          setupGuide.setProperties({
            integrationConnectedAt: null,
            integrationMethod: null,
          });
          // Don't stop the rest of this task from running if this fails
          setupGuide.save().catch();
        }
      }
    } catch (err) {
      if (typeof err === 'string') {
        this.set('alert', {
          type: 'error',
          title: 'Something went wrong',
          body: 'When we tried to authorize with segment either the window was closed or that account is no longer authorized. Please try again.',
        });
      } else if ('errors' in err) {
        this.handleOAuthResponse(err);
        this.set('modal', ModalState.Closed);
      } else {
        this.set('alert', {
          type: 'error',
          title: 'Something went wrong',
          body: `Something went wrong when we tried to ${
            method === SegmentAuthAction.Disable ? 'disable' : 'delete'
          } your Segment connection. Please try again.`,
        });
      }
    }
  });

  loadSetupGuide = task(async () => {
    return this.store.findRecord('setup-guide', 'current');
  });

  clearConnection = task(async () => {
    await this.segmentAuth.deleteConnection.perform(SegmentIntegration.Destination);
    await this.segmentAuth.segmentIn.perform();

    this.set('modal', ModalState.Closed);
  });

  @action
  handleOAuthResponse(response: SegmentAuthenticateResponse) {
    if (response.state === SegmentAuthResult.Created && response.userAction === SegmentAuthAction.Check) {
      this.setProperties({
        alert: {
          type: 'success',
          title: 'Everything looks good!',
          body: `We've verified your workspace connection to Segment.`,
        },
        latestAuthenticateResponse: response,
      });
    } else if (response.state === SegmentAuthResult.Created) {
      this.setProperties({
        alert: {
          type: 'success',
          body: `You've connected your Customer.io workspace to your Segment account.`,
        },
        latestAuthenticateResponse: response,
      });
    } else if (response.state === SegmentAuthResult.Enabled) {
      this.setProperties({
        alert: {
          type: 'success',
          body: `You've re-enabled your Customer.io workspace to your Segment account.`,
        },
        latestAuthenticateResponse: response,
      });
    } else if (response.state === SegmentAuthResult.Disabled) {
      this.setProperties({
        alert: {
          type: 'success',
          body: "We've disabled your connection to Segment. To re-enable your connection, select the <em>Re-enable Segment</em> button below.",
        },
        latestAuthenticateResponse: response,
      });
    } else if (response.state === SegmentAuthResult.Deleted) {
      this.setProperties({
        alert: {
          type: 'success',
          body: "We've deleted your connection to Segment.",
        },
        latestAuthenticateResponse: response,
      });
    } else if (response.enabled) {
      this.setProperties({
        alert: {
          type: 'success',
          title: 'Everything looks good!',
          body: "We've verified your workspace connection to Segment.",
        },
        latestAuthenticateResponse: response,
      });
    } else if (response.enabled === false) {
      this.setProperties({
        alert: {
          type: 'error',
          title: 'Something went wrong',
          body: 'This workspace is disabled in Segment. Select <em>Re-enable Segment</em> below to re-enable the workspace in Segment.',
        },
        latestAuthenticateResponse: response,
      });
    } else if (response.state === SegmentAuthResult.Errored && response.errors) {
      if (response.errors[0].detail === 'source mismatch') {
        let { existing_source_name, new_source_name } = response.errors[0].meta;
        let existingSource = this.parseSegmentWorkspace(existing_source_name);
        let chosenSource = this.parseSegmentWorkspace(new_source_name);
        let label = this.buttonLabel;

        if (response.userAction === SegmentAuthAction.Delete) {
          label = 'Delete Connection from the <em>Options</em> menu';
        } else if (response.userAction === SegmentAuthAction.Disable) {
          label = 'Disable Connection from the <em>Options</em> menu';
        }

        this.set('alert', {
          type: 'error',
          title: `Please authorize ${existingSource}`,
          body: `To ${response.userAction} <strong>${existingSource}</strong>, please select it instead of <strong>${chosenSource}</strong> when authorizing in Segment. Select <em>${label}</em> below to try again.`,
        });
      } else if (response.errors[0].detail === 'destination mismatch') {
        let { site_id: siteId, environment_id, source } = response.errors[0].meta;
        let environmentId = environment_id?.toString();

        if (!environmentId) {
          this.set('alert', {
            type: 'error',
            title: `Heads up!`,
            body: `<strong>${this.parseSegmentWorkspace(
              source,
            )}</strong> is already connected to another Customer.io workspace. Please delete it from all other workspaces, or choose another Segment Source.`,
          });
        } else if (environmentId !== this.environment.current.id) {
          let otherEnv = this.appController.environmentOptions?.find(
            (environment) => environment.value === environmentId,
          );

          this.set('alert', {
            type: 'error',
            title: `Heads up!`,
            body: `<strong>${this.parseSegmentWorkspace(
              source,
            )}</strong> is already connected to another Customer.io workspace. Please delete it from <strong>${
              otherEnv?.label ?? 'all other workspaces'
            }</strong>, or choose another Segment Source.`,
          });
        } else if (this.selectedCredential?.siteId !== siteId) {
          let otherCred = this.store.peekAll('api-credential').findBy('siteId', siteId);

          this.set('alert', {
            type: 'error',
            title: `Heads up!`,
            body: `<strong>${this.parseSegmentWorkspace(
              source,
            )}</strong> is already connected with another credential, <strong>${otherCred?.name}</strong>. Please choose <strong>${
              otherCred?.name ?? 'another credential'
            }</strong> from the credentials dropdown.`,
          });
        }
      } else if (response.errors[0].status === '404') {
        this.setProperties({
          alert: {
            type: 'error',
            title: 'Something went wrong',
            body: "We're unable to find this workspace in Segment. Select <em>Configure Segment</em> below to reconnect.",
          },
          latestAuthenticateResponse: response,
        });
      }
    }
  }

  // Segment workspace/source combos come back as such:
  //   workspaces/:workspace/sources/:source
  @action
  parseSegmentWorkspace(fullPath: string) {
    let [, workspace, , source] = fullPath.split('/');

    return `${workspace}/${source}`;
  }

  @action
  closeAlert() {
    this.set('alert', null);
  }

  @action
  showDeleteModal() {
    this.analytics.track('integration', 'disable_started', {
      ...INTEGRATION_META,
      disabledOrDeleted: 'Deleted',
    });

    this.set('modal', ModalState.Delete);
  }

  @action
  showDisableModal() {
    this.analytics.track('integration', 'disable_started', {
      ...INTEGRATION_META,
      disabledOrDeleted: 'Disabled',
    });

    this.set('modal', ModalState.Disable);
  }

  @action
  showClearModal() {
    this.analytics.track('integration', 'clear_started', INTEGRATION_META);

    this.set('modal', ModalState.Clear);
  }

  @action
  hideModal() {
    this.set('modal', ModalState.Closed);
  }

  @action
  integrationLinkClicked() {
    let trackData: Record<string, string | boolean> = {
      from: 'In-App',
      name: 'Segment',
      category: 'Data management',
      enabled: !!this.integrationsController.segmentOut.last?.value,
      type: 'Native',
      subtitle: 'Source',
    };

    this.analytics.track('integration', 'clicked', trackData);
  }
}
